import {useMemo} from 'react';

import {isNotNil} from 'ramda';

import {useGetAllMakesModelsQuery} from '@dms/api/sales';

import {useApiEnums} from './useApiEnums';

export type AuctionEnumLookup = ReadonlyMap<string, string>;

const ENUM_PROPERTIES = [
  'transmission',
  'fuel_type',
  'drive',
  'car_style',
  'feature',
  'door_count',
  'interior_material',
] as const;

/**
 * Builds a consolidated `Map<string, string>` that resolves raw enum `const_key`
 * values (e.g. `MAKE_PORSCHE`, `TRANSMISSION_MANUAL`) to human-readable labels.
 *
 * Sources:
 * - `useApiEnums` for transmission, fuel_type, drive, car_style, door_count, interior_material
 * - `useGetAllMakesModelsQuery` for makes, models, and model families
 *
 * The map is safe from key collisions because each enum category uses a distinct
 * prefix (`MAKE_`, `TRANSMISSION_`, `FUELTYPE_`, `DRIVE_`, `CARSTYLE_`, etc.).
 */
export function useAuctionEnumLookup(): AuctionEnumLookup {
  const {getEnum} = useApiEnums({flat: true});
  const {data: makesModelsData} = useGetAllMakesModelsQuery({
    vehicleType: 'VEHICLETYPE_PASSENGER_CAR',
  });

  return useMemo(() => {
    const map = new Map<string, string>();

    for (const property of ENUM_PROPERTIES) {
      for (const item of getEnum(property)) {
        map.set(item.key, item.label);
      }
    }

    if (isNotNil(makesModelsData)) {
      for (const [key, make] of Object.entries(makesModelsData.makes)) {
        map.set(key, make.label);
      }
      for (const [key, model] of Object.entries(makesModelsData.models)) {
        map.set(key, model.label);
      }
      for (const [key, family] of Object.entries(makesModelsData.modelFamilies)) {
        map.set(key, family.label);
      }
    }

    return map;
  }, [getEnum, makesModelsData]);
}
